import type { ReactNode } from 'react';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

/* ----------------------------------------------------------------------------
 * Shared data-lifecycle states (design spec v1.1 §9 "Feedback/empty states").
 *
 * - EmptyState: "empty states teach" — serif headline + 1-line teach + CTA.
 * - ErrorState: denials read as human guidance, never a raw 403; network
 *   failures are non-blocking and retryable.
 * - LoadingState: neutral skeleton rows (no copper shimmer — that is reserved
 *   for AI surfaces).
 * Token-styled and deliberately minimal — the visual system is a placeholder.
 * -------------------------------------------------------------------------- */

export function LoadingState({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('space-y-3 py-4', className)} role="status" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-10 animate-pulse rounded-md bg-inset" />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  teach,
  action,
  className,
}: {
  title: string;
  /** One line that teaches what will appear here and how it gets there. */
  teach: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border border-dashed border-border-default bg-surface px-6 py-14 text-center',
        className,
      )}
    >
      <h2 className="font-display text-xl font-semibold tracking-tight text-text-primary">
        {title}
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-text-secondary">{teach}</p>
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  );
}

/** Guidance copy for an error. 403 → human guidance; 0 → network; else generic. */
function errorCopy(error: unknown): { title: string; detail: string } {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return {
        title: 'This area isn’t available to you yet',
        detail:
          'Your workspace or role doesn’t include access to this data. If you think it should, ask your workspace owner to enable it — nothing is broken.',
      };
    }
    if (error.status === 0) {
      return {
        title: 'Can’t reach BookedCentral right now',
        detail:
          'The connection to the data service failed. Check your network and try again — nothing has been lost.',
      };
    }
    return {
      title: 'Something went wrong loading this data',
      detail: error.message || 'The data service returned an unexpected response. Try again.',
    };
  }
  return {
    title: 'Something went wrong loading this data',
    detail: error instanceof Error ? error.message : 'Unexpected error. Try again.',
  };
}

export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const { title, detail } = errorCopy(error);
  const denied = error instanceof ApiError && error.status === 403;
  return (
    <div
      role="alert"
      className={cn(
        'rounded-lg border bg-surface px-6 py-12 text-center',
        denied ? 'border-border-subtle' : 'border-warning/30',
        className,
      )}
    >
      <h2 className="font-display text-xl font-semibold tracking-tight text-text-primary">
        {title}
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-text-secondary">{detail}</p>
      {onRetry && !denied && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-5 inline-flex h-control items-center rounded-md border border-border-default bg-surface px-3 text-sm font-medium text-text-primary transition-colors duration-fast hover:bg-hover"
        >
          Try again
        </button>
      )}
    </div>
  );
}
